import React, { useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import { getRevenueMargins } from '../../api/salesOffers';
import DirectCostsPage from '../financials/DirectCostsPage';

/* REVENUS & MARGES (2026-09-12) : le compte de résultat du revenu lu contre le
   PLAN MENSUEL, dans l'habillage de Coûts des ventes, en lecture seule. Les
   comptes de chiffre d'affaires comme dans Finance (le réel jusqu'à la
   clôture, le Plan mensuel au-delà, sans détail par offre), puis les charges
   directes avec leur règle et leur projection contre ce revenu, la valeur
   ajoutée en pied. La vue vient du serveur ; ici on la met au format du compte
   de résultat, et c'est l'écran de Coûts des ventes qui la dessine. Les RÈGLES
   des charges (les mêmes que dans Coûts des ventes, portées par la ligne) s'y
   règlent ; les mois, eux, sont calculés ici et ne se tapent pas. */
const SANS = '__sans_compte__';

export default function RevenueMarginsPage({ activeTenant, year, unit = 'kchf', canWrite = false }) {
  const p = useP();
  const tid = activeTenant?.id;
  const an = Number(year) || new Date().getFullYear();
  const [vue, setVue] = useState(null);
  const [tour, setTour] = useState(0);   // relecture après qu'une règle a changé

  useEffect(() => {
    let vivant = true;
    if (!tid) return () => {};
    getRevenueMargins(tid, an).then((r) => { if (vivant && !r.error) setVue(r.data); }).catch(() => {});
    return () => { vivant = false; };
  }, [tid, an, tour]);

  const data = useMemo(() => {
    if (!vue) return null;
    const cc = vue.closed_count;
    const mois = (x, signe = 1) => Object.fromEntries(Array.from({ length: 12 }, (_, i) => {
      const v = i < cc ? (x.actual ? x.actual[i] : null) : (x.plan ? x.plan[i] : null);
      return [String(i + 1), v == null ? 0 : signe * v];
    }));
    const lines = [
      ...vue.revenue.lines.map((l) => ({ id: l.id, label: l.label, section: 'revenue', hidden: false, line_role: null })),
      ...(vue.revenue.unassigned ? [{ id: SANS, label: p('Sans compte'), section: 'revenue', hidden: false, line_role: null }] : []),
      ...vue.cogs.lines.map((l) => ({ id: l.id, label: l.label, section: 'cogs', hidden: false, line_role: l.line_role, stock_linked: l.stock_linked,
                                      forecast_method: l.method, forecast_rate: l.rate, forecast_base_line_id: l.base_line_id, forecast_target: l.target })),
    ];
    const rows = [
      ...vue.revenue.lines.map((l) => ({ type: 'detail', code: l.id, months: mois(l) })),
      ...(vue.revenue.unassigned ? [{ type: 'detail', code: SANS, months: mois(vue.revenue.unassigned) }] : []),
      ...vue.cogs.lines.map((l) => ({ type: 'detail', code: l.id, months: mois(l, -1) })),   // charges en négatif, comme au compte de résultat
    ];
    // La consommation du plan, mois ouverts, en négatif : lue dans le popover d'Achats.
    const plan_goods = Object.fromEntries((vue.cogs.plan_goods || []).map((v, i) => [String(i + 1), v == null ? 0 : -v]).filter(([m]) => Number(m) > cc));
    return { year: vue.year, closed_count: cc, lines, rows, plan_goods };
  }, [vue, p]);

  if (!tid) return null;
  if (!data) return <div className="om-empty">{p('Chargement…')}</div>;
  return (
    <DirectCostsPage embedded data={data} title={p('Revenus & Marges')} activeTenant={activeTenant} activeYear={an}
                     canWrite={canWrite} cellsEditable={false} unit={unit} onChanged={() => setTour((n) => n + 1)} />
  );
}
